// src/pages/Users/Absences/AbsencesPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "./../../../components/Button/Button";
import DashboardNavigation from "./../../DashboardNavigation/DashboardNavigation";
import {
  getAdherentsByUser,
  updateBlessures,
} from "./../../../services/adherentServices";
import { useUser } from "./../../Home/UserContext";
import { getRoleFromRoleId } from "./../../../utils/roleHelper";
import useScrollToTop from "./../../../hooks/useScollToTop";
import { ToastContainer, toast } from "react-toastify";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { FaCheckCircle } from "react-icons/fa";

const INFO_TXT =
  "Pour un souci d’organisation, nous demandons aux parents de signaler les absences (plus d’un cours) aux enseignants et au bureau. Merci d’avance. Pensez à ne plus signaler l’adhérent comme absent dès qu’il a repris les cours.";

function InfoBanner() {
  return (
    <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-800/70 bg-blue-50/80 dark:bg-slate-800/60 backdrop-blur">
      <div className="px-4 py-4 sm:px-6">
        <p className="text-sm sm:text-base text-slate-800 dark:text-slate-100 leading-relaxed">
          {INFO_TXT}
        </p>
      </div>
    </div>
  );
}

// Mini fiche adhérent + action "Marquer comme repris"
function SelectedAdherentCard({ adherent, onMarkReturned, busy }) {
  if (!adherent) return null;

  const canMarkReturned = !!(adherent.is_blessed || adherent.date_reprise);

    const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 rounded-xl bg-slate-50 dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700">
      {/* bloc info */}
      <div className="flex items-start sm:items-center gap-4 min-w-0">
        <img
          src={toAbsolute(adherent.photo_url) || "/img/user_avatar.jpg"}
          alt={`${adherent.prenom} ${adherent.nom}`}
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover ring-1 ring-slate-200 dark:ring-slate-700"
        />
        <div className="min-w-0">
          <p className="font-semibold text-slate-900 dark:text-white truncate">
            {adherent.prenom} {adherent.nom}
          </p>

          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span
              className={`inline-flex items-center rounded-full px-2 py-[2px] text-[11px] font-semibold ring-1 ${
                adherent.is_blessed
                  ? "bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-900/40 dark:text-amber-200 dark:ring-amber-800"
                  : "bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-200 dark:ring-emerald-800"
              }`}
            >
              Statut : {adherent.is_blessed ? "Absent(e)" : "Actif(ve)"}
            </span>

            {adherent.date_reprise && (
              <span className="text-[11px] text-slate-500 dark:text-slate-400">
                Reprise prévue :{" "}
                {new Date(adherent.date_reprise).toLocaleDateString("fr-FR")}
              </span>
            )}
          </div>

          {adherent.notes_adherent && (
            <p className="text-[11px] mt-1 text-slate-500 dark:text-slate-400 line-clamp-2">
              Notes : {adherent.notes_adherent}
            </p>
          )}
        </div>
      </div>

      {/* bouton action */}
      <div className="sm:ml-4 w-full sm:w-auto">
        <Button
          type="button"
          text="Marquer comme repris"
          icon={FaCheckCircle}
          onClick={onMarkReturned}
          disabled={!canMarkReturned || busy}
          className="!w-full sm:!w-auto !rounded-xl !bg-emerald-600 !text-white hover:!bg-emerald-700 disabled:!bg-slate-400 disabled:cursor-not-allowed gap-2"
        />
      </div>
    </div>
  );
}

export default function AbsencesPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  // Données
  const [adherents, setAdherents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form
  const [selectedAdherentId, setSelectedAdherentId] = useState("");
  const [isBlessed, setIsBlessed] = useState(false);
  const [dateReprise, setDateReprise] = useState("");
  const [motifAbsence, setMotifAbsence] = useState("");
  const [saving, setSaving] = useState(false);
  const [marking, setMarking] = useState(false);

  // Charger les adhérents
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        if (!user?.userId) {
          toast.error("Utilisateur non identifié.");
          return;
        }
        const data = await getAdherentsByUser(user.userId);
        setAdherents(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Erreur récupération adhérents:", e);
        toast.error("Impossible de charger vos adhérents.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user?.userId]);

  const selectedAdherent = useMemo(
    () => adherents.find((a) => String(a.id) === String(selectedAdherentId)),
    [adherents, selectedAdherentId]
  );

  // Hydrate à la sélection
  useEffect(() => {
    if (!selectedAdherent) return;
    setIsBlessed(!!selectedAdherent.is_blessed);
    setDateReprise(
      selectedAdherent.date_reprise
        ? selectedAdherent.date_reprise.split("T")[0]
        : ""
    );
    setMotifAbsence("");
  }, [selectedAdherent]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAdherent) {
      toast.warn("Choisissez d’abord un adhérent.");
      return;
    }
    if (!isBlessed && !motifAbsence && !dateReprise) {
      toast.warn("Merci de préciser au moins un motif ou une date de reprise.");
      return;
    }

    const today = new Date().toLocaleDateString("fr-FR");
    const formattedDate = dateReprise
      ? new Date(dateReprise).toLocaleDateString("fr-FR")
      : "non précisée";
    const newNote = `Absence signalée le ${today} jusqu'au ${formattedDate} : ${
      motifAbsence || "—"
    }`;

    const newNotes = selectedAdherent.notes_adherent
      ? `${selectedAdherent.notes_adherent} | ${newNote}`
      : newNote;

    try {
      setSaving(true);
      await updateBlessures(selectedAdherent.id, {
        is_blessed: isBlessed,
        date_reprise: dateReprise || null,
        notes_adherent: newNotes,
      });

      // maj locale
      setAdherents((prev) =>
        prev.map((a) =>
          a.id === selectedAdherent.id
            ? {
                ...a,
                is_blessed: isBlessed,
                date_reprise: dateReprise || null,
                notes_adherent: newNotes,
              }
            : a
        )
      );

      toast.success("Absence signalée avec succès.");
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // Nouveau : Marquer comme repris
  const handleMarkReturned = async () => {
    if (!selectedAdherent) return;
    const ok = window.confirm(
      `Confirmer la reprise pour ${selectedAdherent.prenom} ${selectedAdherent.nom} ?`
    );
    if (!ok) return;

    const today = new Date().toLocaleDateString("fr-FR");
    const note = `Reprise signalée le ${today}`;
    const newNotes = selectedAdherent.notes_adherent
      ? `${selectedAdherent.notes_adherent} | ${note}`
      : note;

    try {
      setMarking(true);
      await updateBlessures(selectedAdherent.id, {
        is_blessed: false,
        date_reprise: null,
        notes_adherent: newNotes,
      });

      // maj locale + synchronisation formulaire
      setAdherents((prev) =>
        prev.map((a) =>
          a.id === selectedAdherent.id
            ? {
                ...a,
                is_blessed: false,
                date_reprise: null,
                notes_adherent: newNotes,
              }
            : a
        )
      );
      setIsBlessed(false);
      setDateReprise("");

      toast.success("Reprise enregistrée.");
    } catch (e) {
      console.error(e);
      toast.error("Impossible d’enregistrer la reprise.");
    } finally {
      setMarking(false);
    }
  };

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Signaler une absence"
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur">
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex items-center justify-between">
              <h4 className="text-lg sm:text-xl font-semibold">
                Absences / Maladies / Blessures
              </h4>
              <span className="text-white/90 text-sm">
                Organisation du club
              </span>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="mb-5">
              <InfoBanner />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1fr,320px] gap-6">
              {/* Formulaire */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                    Sélectionner un adhérent
                  </label>
                  <select
                    className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                    value={selectedAdherentId}
                    onChange={(e) => setSelectedAdherentId(e.target.value)}
                    disabled={loading || adherents.length === 0}
                  >
                    <option value="">— Choisir —</option>
                    {adherents.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.prenom} {a.nom}
                      </option>
                    ))}
                  </select>
                  {loading && (
                    <p className="text-xs text-slate-500 mt-1">
                      Chargement des adhérents…
                    </p>
                  )}
                </div>

                <SelectedAdherentCard
                  adherent={selectedAdherent}
                  onMarkReturned={handleMarkReturned}
                  busy={marking}
                />

                <form onSubmit={handleSubmit} className="space-y-4">
                  <label className="flex items-center gap-2 rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-800/70 px-3 py-2 w-fit">
                    <input
                      type="checkbox"
                      checked={isBlessed}
                      onChange={(e) => setIsBlessed(e.target.checked)}
                      className="accent-violet-500"
                    />
                    <span className="text-sm text-slate-800 dark:text-slate-100">
                      Blessé(e) / Malade / Absent(e)
                    </span>
                  </label>

                  <div>
                    <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                      Date de reprise estimée
                    </label>
                    <input
                      type="date"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      value={dateReprise}
                      onChange={(e) => setDateReprise(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-800 dark:text-slate-100 mb-1">
                      Motif d’absence (facultatif)
                    </label>
                    <textarea
                      rows={3}
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-400"
                      placeholder="Ex. : grippe, blessure au genou, déplacement familial…"
                      value={motifAbsence}
                      onChange={(e) => setMotifAbsence(e.target.value)}
                    />
                  </div>

                  <div className="flex items-center justify-end">
                    <Button
                      type="submit"
                      text={
                        saving ? "Enregistrement…" : "Enregistrer l’absence"
                      }
                      className="!rounded-xl"
                      disabled={!selectedAdherent || saving}
                    />
                  </div>
                </form>
              </div>

              {/* Illustration contrainte */}
              <div className="flex items-start justify-center">
                <div className="w-full max-w-[280px] lg:max-w-[320px]">
                  <img
                    src="/img/blessed.webp"
                    alt="Judoka blessé"
                    className="w-full h-auto object-contain rounded-xl shadow ring-1 ring-slate-200 dark:ring-slate-700"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <ToastContainer />
      </div>
    </section>
  );
}
